import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
};
const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export const AuthProvider = ({ children }) => {
    const [state, setState] = useState(initialState);
    const navigate = useNavigate();
    // Session validation on mount
    useEffect(() => {
        const validateSession = async () => {
            try {
                const response = await fetch('/api/auth/validate', {
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.user) {
                        setState(prev => ({
                            ...prev,
                            user: data.user,
                            isAuthenticated: true,
                            isLoading: false
                        }));
                    }
                    else {
                        setState(prev => ({
                            ...prev,
                            user: null,
                            isAuthenticated: false,
                            isLoading: false
                        }));
                    }
                }
                else {
                    setState(prev => ({
                        ...prev,
                        user: null,
                        isAuthenticated: false,
                        isLoading: false
                    }));
                }
            }
            catch (error) {
                console.error('Session validation error:', error);
                setState(prev => ({
                    ...prev,
                    user: null,
                    isAuthenticated: false,
                    isLoading: false
                }));
            }
        };
        validateSession();
    }, []);
    const login = useCallback(async (credentials) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(credentials),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }
            setState({
                user: data.user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
            });
            // Navigate to dashboard after successful login
            navigate('/dashboard');
        }
        catch (error) {
            console.error('Login error:', error);
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: error.message || 'Login failed. Please try again.',
            }));
            throw error;
        }
    }, [navigate]);
    const register = useCallback(async (credentials) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Registration failed');
            }
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: null,
            }));
            // Navigate to verification page
            navigate('/verify-email', { state: { email: credentials.email } });
        }
        catch (error) {
            console.error('Registration error:', error);
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: error.message || 'Registration failed. Please try again.',
            }));
            throw error;
        }
    }, [navigate]);
    const logout = useCallback(async () => {
        try {
            await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include',
            });
            setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
            });
            navigate('/login');
        }
        catch (error) {
            console.error('Logout error:', error);
            // Still clear local state even if API call fails
            setState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
            });
            navigate('/login');
        }
    }, [navigate]);
    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);
    const setUser = useCallback((user) => {
        setState(prev => ({
            ...prev,
            user,
            isAuthenticated: true,
        }));
    }, []);
    const value = {
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        error: state.error,
        login,
        register,
        logout,
        clearError,
        setUser,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
};
